/**
 * Publish this workstation's shared printers to the global device registry.
 *
 * Lets other PCs discover printers (with the host auto-filled) without anyone
 * manually adding them in Settings → Devices. Runs from any authenticated
 * dashboard session (app start and the Devices page). Idempotent: existing
 * registry rows and local device ids are reused, so repeated runs don't create
 * duplicates.
 */
import { getDevices } from './devices';
import { settingsService, DevicePayload } from '../services/httpServices/settingsService';
import { agentDetectPrinters, getPrintAgentHostname, isPrintAgentAvailable } from './printAgent';

/** Split a UNC printer port (`\\HOST\SHARE`) into its host and share name. */
export function parseUncPort(portName: string): { host: string; share: string } | null {
  const p = (portName || '').trim().replace(/\//g, '\\');
  if (!p.startsWith('\\\\')) return null;
  const parts = p.slice(2).split('\\').filter(Boolean);
  if (parts.length < 2) return null;
  return { host: parts[0], share: parts[1] };
}

/** Stable id so auto-published registry entries upsert instead of duplicating. */
export function autoDeviceId(host: string, share: string): string {
  return `auto_${host}_${share}`
    .replace(/[^A-Za-z0-9_.-]/g, '_')
    .toLowerCase()
    .slice(0, 100);
}

let inFlight: Promise<void> | null = null;

/**
 * Detect this PC's shared printers and upsert them into the registry, tagged
 * with the agent's hostname. Safe to call repeatedly or concurrently.
 */
export async function publishLocalPrinters(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      if (!(await isPrintAgentAvailable())) return;
      const hostname = await getPrintAgentHostname();
      if (!hostname) return;

      const detected = await agentDetectPrinters();
      if (!detected) return;
      // Only printers shared *from this PC*: a network printer installed here
      // (port `\\other-pc\share`) belongs to the other PC, not this one.
      const shared = detected.filter((p) => p.shared && p.shareName && !parseUncPort(p.portName));
      if (shared.length === 0) return;

      let registry: DevicePayload[] = [];
      try {
        registry = (await settingsService.listDevices()).data || [];
      } catch {
        /* offline — publish with derived ids anyway */
      }

      const locals = getDevices();
      for (const p of shared) {
        const share = p.shareName.toLowerCase();
        const alreadyPublished = registry.some(
          (d) =>
            (d.workstation || '').toLowerCase() === hostname.toLowerCase() &&
            (d.shareName || '').toLowerCase() === share,
        );
        if (alreadyPublished) continue;

        const local = locals.find((d) => (d.shareName || '').toLowerCase() === share);
        // A row synced before the agent identity was known has a share but no
        // host. Reuse it (by clientId) so this upsert backfills the workstation
        // instead of leaving an untargetable row and creating a duplicate.
        const hostless = registry.find(
          (d) =>
            (d.shareName || '').toLowerCase() === share &&
            !(d.networkIp || d.pcName || d.workstation || '').trim(),
        );
        settingsService
          .upsertDevice({
            clientId: local?.id || hostless?.clientId || autoDeviceId(hostname, p.shareName),
            name: local?.name || hostless?.name || p.name || p.shareName,
            type: local?.type || hostless?.type || 'printer',
            shareName: p.shareName,
            workstation: hostname,
            enabled: true,
            isReceiptPrinter: local?.isReceiptPrinter || false,
            isLabelPrinter: local?.isLabelPrinter || false,
            isDiagnosticReceiptPrinter: local?.isDiagnosticReceiptPrinter || false,
            isDocumentPrinter: local?.isDocumentPrinter || false,
          })
          .catch(() => {
            /* offline / no permission — ignore */
          });
      }
    } catch {
      /* agent offline or detection failed — ignore */
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}
